import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  TagIcon, 
  ClipboardDocumentListIcon, 
  TruckIcon, 
  ArchiveBoxIcon, 
  ChartBarIcon,
  ArrowDownOnSquareIcon
} from '@heroicons/react/24/outline';
import { message } from 'antd';

// Importation des composants
import ProductList from '../components/products/ProductList';
import ProductForm from '../components/products/ProductForm';
import StockManagement from '../components/products/StockManagement';
import InventoryAnalytics from '../components/products/InventoryAnalytics';
import SupplyManagement from '../components/products/SupplyManagement';
import InventoryManagement from '../components/products/InventoryManagement';
import ShopifyImport from '../components/products/ShopifyImport';
import PlanGate from '../components/common/PlanGate';
import { productsApi } from '../utils/api';
import usePlan from '../hooks/usePlan';

const Products = () => {
  const [activeTab, setActiveTab] = useState('catalogue');
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showShopifyImport, setShowShopifyImport] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const { canCreate } = usePlan();

  // Charger les produits au montage
  // useEffect(() => {
  //   loadProducts();
  // }, []);

  /**
   * Charger la liste des produits
   */
  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await productsApi.getAll();
      setProducts(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
      message.error('Erreur lors du chargement des produits');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProduct = (product) => {
    // Dans une application réelle, ici on enverrait les données à l'API
    console.log('Produit sauvegardé:', product);
    setShowProductForm(false);
    setEditingProduct(null);
    // Recharger la liste des produits
    loadProducts();
  };

  /**
   * Gérer la création d'un nouveau produit avec vérification des limites
   */
  const handleNewProduct = async () => {
    try {
      // Vérifier les limites du plan
      const limitCheck = await canCreate('products', products.length);
      
      if (!limitCheck.allowed) {
        message.warning(
          `Limite atteinte: ${limitCheck.current}/${limitCheck.limit} produits autorisés sur votre plan.`
        );
        return;
      }

      setEditingProduct(null);
      setShowProductForm(true);
    } catch (error) {
      console.error('Erreur lors de la vérification des limites:', error);
      message.error('Erreur lors de la vérification des limites');
    }
  };

  /**
   * Gérer l'import Shopify avec vérification des limites
   */
  const handleShopifyImport = async () => {
    try {
      // Vérifier les limites du plan
      const limitCheck = await canCreate('products', products.length);
      
      if (!limitCheck.allowed) {
        message.warning(
          `Vous ne pouvez pas importer de nouveaux produits. Limite atteinte: ${limitCheck.current}/${limitCheck.limit} produits.`
        );
        return;
      }

      setShowShopifyImport(true);
    } catch (error) {
      console.error('Erreur lors de la vérification des limites:', error);
      message.error('Erreur lors de la vérification des limites');
    }
  };

  const handleShopifyImportComplete = (importedProducts, count) => {
    // Dans une application réelle, ici on enverrait les données à l'API pour ajouter les produits
    console.log(`${count} produits importés depuis Shopify:`, importedProducts);
    message.success(`${count} produits importés avec succès`);
    // Recharger la liste des produits
    loadProducts();
  };

  const tabs = [
    { name: 'Catalogue', value: 'catalogue', icon: TagIcon },
    { name: 'Stocks', value: 'stocks', icon: ArchiveBoxIcon },
    // { name: 'Approvisionnement', value: 'approvisionnement', icon: TruckIcon },
    // { name: 'Inventaire', value: 'inventaire', icon: ClipboardDocumentListIcon },
    // { name: 'Analyse', value: 'analyse', icon: ChartBarIcon },
  ];

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Gestion des Produits</h1>
        <div className="flex space-x-3">
          {/* Import Shopify avec contrôle de plan */}
          <PlanGate 
            feature="shopify_import" 
            type="feature"
            fallback={
              <button
                type="button"
                disabled
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-400 bg-gray-100 cursor-not-allowed"
              >
                <ArrowDownOnSquareIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                Import Shopify (Plan requis)
              </button>
            }
          >
            <button
              type="button"
              onClick={handleShopifyImport}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <ArrowDownOnSquareIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" aria-hidden="true" />
              Importer depuis Shopify
            </button>
          </PlanGate>

          {/* Bouton Nouveau Produit avec contrôle de limitation */}
          <PlanGate 
            type="limit" 
            limitType="products" 
            currentCount={products.length}
            fallback={
              <button
                type="button"
                disabled
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-gray-400 bg-gray-200 cursor-not-allowed"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                Limite atteinte
              </button>
            }
          >
            <button
              type="button"
              onClick={handleNewProduct}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              Nouveau Produit
            </button>
          </PlanGate>
        </div>
      </div>

      {/* Formulaire de produit (modal) */}
      {showProductForm && (
        <ProductForm 
          product={editingProduct}
          onClose={() => setShowProductForm(false)}
          onSave={handleSaveProduct}
        />
      )}

      {/* Modal d'importation Shopify */}
      {showShopifyImport && (
        <ShopifyImport
          onClose={() => setShowShopifyImport(false)}
          onImportComplete={handleShopifyImportComplete}
        />
      )}

      {/* Onglets */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={`
                ${activeTab === tab.value
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
                group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
              `}
            >
              <tab.icon
                className={`
                  ${activeTab === tab.value ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'}
                  -ml-0.5 mr-2 h-5 w-5
                `}
                aria-hidden="true"
              />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Contenu des onglets */}
      <div className="mt-6">
        {activeTab === 'catalogue' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Catalogue de Produits</h3>
              <p className="mt-1 text-sm text-gray-500">
                Gérez vos produits, catégories, variations, prix et descriptions.
              </p>
              <div className="mt-6">
                <ProductList 
                  // products={products} 
                  // loading={loading}
                  // onProductUpdate={loadProducts}
                /> 
              </div>
            </div>
          </div>
        )}

        {activeTab === 'stocks' && (
          <PlanGate 
            feature="inventory_sync" 
            type="feature"
            fallback={null}
          >
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Gestion des Stocks</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Suivez vos stocks disponibles, mouvements et configurez les seuils d'alerte.
                </p>
                <div className="mt-6">
                   <StockManagement /> 
                </div>
              </div>
            </div>
          </PlanGate>
        )}

        {activeTab === 'approvisionnement' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Approvisionnement</h3>
              <p className="mt-1 text-sm text-gray-500">
                Gérez vos fournisseurs, bons de commande et réceptions de marchandises.
              </p>
              <div className="mt-6">
                <SupplyManagement />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'inventaire' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Inventaire</h3>
              <p className="mt-1 text-sm text-gray-500">
                Planifiez et réalisez vos inventaires, réconciliez les écarts.
              </p>
              <div className="mt-6">
                <InventoryManagement />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analyse' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Analyse et Reporting</h3>
              <p className="mt-1 text-sm text-gray-500">
                Consultez les statistiques de rotation des stocks, valorisation et analyses de performance.
              </p>
              <div className="mt-6">
                <InventoryAnalytics />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Products; 